/**
 * `kioskconfiguration/:kioskConfigurationId/fetch` → `KioskConfig`.
 *
 * The API's object is the merchant panel's EDITING shape — the same document `save` takes back,
 * with `_id` renamed `mid` — so nothing on it is named the way the kiosk reads it. This file is
 * the one translation between the two; everything downstream keeps reading `KioskConfig` and
 * never sees the wire shape.
 *
 * Pure — no store, no platform API — so the saga that calls it and the tests that pin it share it.
 *
 * THE CONTRACT is rista-be `sales-services`' `kiosk_configuration_service.validateStructure`
 * (branch DP-7554): every media-bearing part is a SECTION shaped `{ settings, media }`, declared
 * once in its `SECTIONS` table.
 *
 * | API | rule (backend) | `KioskConfig` |
 * |---|---|---|
 * | `groupBindings.dineInChannelDefId` / `takeawayChannelDefId` | required, at least one | NOT READ — see below |
 * | `screensaver.media` | required, non-empty | `screensaver` |
 * | `screensaver.settings.idleTimeoutSeconds` | integer 10–600 | `idleTimeoutSeconds` |
 * | `screensaver.settings.slideDurationSeconds` | integer 1–600 | `screensaverSlideSeconds` |
 * | `banners.media` (+ `metadata.categoryId` / `subCategoryId`) | optional | `banners` |
 * | `banners.settings.slideDurationSeconds` | integer 1–600 | `bannerSlideSeconds` |
 * | `branding.settings.primaryColor` | ≤ 9 chars | `primaryColor` |
 * | `branding.settings.layout` | not validated — the panel's own key | `layout` |
 * | `branding.media` `role: "logo"` | optional | `logo` |
 * | `catalogue.media` `role: "itemPlaceholder"` | optional | `itemPlaceholder` |
 * | `customer.captureMobile` | `"off" \| "optional" \| "mandatory"` | `customerPhoneNumber` |
 * | `customer.digitalBill` | boolean | `digitalBill` |
 *
 * A media item is `{ name, mid, mediaType: "Image" | "Video", role?, metadata? }`. `mid` is an
 * imageData id of type `Kiosk` — the backend checks ownership on save — and it is what the kiosk
 * fetches by. `metadata` is the frontend's own bag (the backend only caps its size and keys); the
 * merchant panel keeps a banner's deep link there. `role` names a SLOT within a section, unique
 * per section: `branding` carries `role: "logo"`, `catalogue` carries `role: "itemPlaceholder"`.
 *
 * 🔴 **The kiosk's services do NOT come from `groupBindings`.** The backend does not reconcile the
 * configuration's pair with the branch's own (a deleted or moved channel is cleaned from one and
 * not the other), so a kiosk reading `groupBindings` can sell on the wrong pair. The branch's pair
 * is `kioskChannels` on `entity/possettings/:branchId` — the POS settings every POS already
 * fetches — and `toKioskServices` below maps it; `kiosk/config/selectors`' `selectConfiguredServices`
 * reads it off `catalogStore.posSettings`.
 *
 * The two role slots are found BY ROLE, never by position: a section's `media` is a list, and the
 * merchant panel is free to put anything else beside the slot. `KioskApp` resolves them into
 * `KioskBrand.logoUrl` / `itemPlaceholderUrl`.
 *
 * Payment modes come from `kioskdevice/fetch`, not from here, so `paymentModeIds` stays empty.
 * `url` on a media row is NOT part of the backend's contract: the panel may store one. An image's
 * `http(s)` url is preferred over its `mid` (`catalogueImageUrl`); a video's is only the fallback.
 */
import type {
  KioskBanner,
  KioskConfig,
  KioskContactPolicy,
  KioskImageSlot,
  KioskServiceConfig,
} from "./types";

export interface KioskConfigurationMedia {
  name?: string;
  /** The media id — an imageData id of type `Kiosk`. Required by the backend. */
  mid?: string;
  /** `"Image"` or `"Video"`. Required by the backend. */
  mediaType?: string;
  /** A named slot within the section (`"logo"`, `"itemPlaceholder"`). Unique per section. */
  role?: string;
  /** Not in the backend contract — see the header. A fallback only. */
  url?: string;
  /**
   * The frontend's own bag, stored verbatim. A banner's deep link lives here — at most one of
   * the two is expected; `categoryId` wins.
   */
  metadata?: {
    categoryId?: string;
    subCategoryId?: string;
    [key: string]: unknown;
  };
}

interface KioskConfigurationSection<Settings> {
  settings?: Settings;
  media?: KioskConfigurationMedia[];
}

/** The response of `kioskconfiguration/:kioskConfigurationId/fetch`, as far as the kiosk reads it. */
export interface KioskConfigurationResponse {
  mid?: string;
  name?: string;
  allBranches?: boolean;
  branches?: string[];
  groupBindings?: {
    dineInChannelDefId?: string;
    takeawayChannelDefId?: string;
  };
  customer?: {
    /** `"off" | "optional" | "mandatory"` — lower case, and `off` rather than `Disabled`. */
    captureMobile?: string;
    digitalBill?: boolean;
  };
  screensaver?: KioskConfigurationSection<{
    idleTimeoutSeconds?: number;
    slideDurationSeconds?: number;
  }>;
  banners?: KioskConfigurationSection<{ slideDurationSeconds?: number }>;
  branding?: KioskConfigurationSection<{
    primaryColor?: string;
    layout?: string;
  }>;
  catalogue?: KioskConfigurationSection<Record<string, unknown>>;
  version?: number;
}

/** Is this the configuration object, rather than an error body or a `{ data }` wrapper? */
export function isKioskConfigurationResponse(payload: any): payload is KioskConfigurationResponse {
  return !!payload && typeof payload === "object" && !payload.code && typeof payload.mid === "string";
}

/**
 * The branch's kiosk channel pair — `kioskChannels` on `entity/possettings/:branchId`. A key that
 * is ABSENT means that order type is not offered; the whole block absent means not set up.
 */
export interface KioskChannels {
  dineInChannelDefId?: string | null;
  takeawayChannelDefId?: string | null;
}

/**
 * The branch's kiosk channels → the services the kiosk offers, in the order it shows them.
 *
 * `id`s are the service KINDS on purpose: `serviceKindOf` reads the id first, so the tile gets
 * the stock Dine-in / Takeaway artwork and its translated label (`ServiceScreen`) without the API
 * sending either. `label` stays empty — the merchant sets no name here, and an English literal in
 * a config would be untranslatable. `channelId` is empty too: `buildServiceList` resolves the
 * branch's channel from `channelDefId`, and drops the service if the branch has none.
 */
export function toKioskServices(bindings: KioskChannels | null | undefined): KioskServiceConfig[] {
  const services: KioskServiceConfig[] = [];
  if (bindings?.dineInChannelDefId) {
    services.push({
      id: "dine-in",
      label: "",
      channelDefId: bindings.dineInChannelDefId,
      channelId: "",
      displayOrder: 1,
    });
  }
  if (bindings?.takeawayChannelDefId) {
    services.push({
      id: "takeaway",
      label: "",
      channelDefId: bindings.takeawayChannelDefId,
      channelId: "",
      displayOrder: 2,
    });
  }
  return services;
}

/**
 * Media rows → banners, in the order the merchant arranged them. A row with neither a `mid` nor
 * a `url` has nothing to draw and is dropped.
 */
function toBanners(media: KioskConfigurationMedia[] | undefined): KioskBanner[] {
  const banners: KioskBanner[] = [];
  (media ?? []).forEach((item, index) => {
    if (!item?.mid && !item?.url) return;
    const isVideo = item.mediaType?.toLowerCase() === "video";
    banners.push({
      id: item.mid || `media-${index + 1}`,
      // An image draws its stored `http(s)` URL when it has one, else by media id like an item
      // card's photo (`catalogueImageUrl`). A video is fetched by media id via `getVideoCdnUrl`,
      // with the stored URL only as the fallback. ⚠️ A stored URL may be a `blob:` preview from
      // the merchant panel's browser, which no other device can load.
      ...(isVideo
        ? { ...(item.mid ? { videoId: item.mid } : {}), videoUrl: item.url }
        : { ...(item.mid ? { imageId: item.mid } : {}), imageUrl: item.url }),
      // The deep link. Ignored by IDLE (a tap there only starts the session); the catalog's
      // rail opens it — `onBannerPress` in `CatalogScreen/useCatalogBrowse`.
      ...(item.metadata?.categoryId ? { categoryId: item.metadata.categoryId } : {}),
      ...(item.metadata?.subCategoryId ? { subCategoryId: item.metadata.subCategoryId } : {}),
      displayOrder: index + 1,
    });
  });
  return banners;
}

/**
 * A section's `role` slot → `KioskImageSlot`, or nothing when the section has no such slot.
 *
 * An IMAGE slot: a `Video` row is not drawable where a logo or a placeholder goes, so it reads as
 * no slot rather than a broken `<Image>`. Like `toBanners`, a row with neither `mid` nor `url` is
 * dropped.
 */
function toImageSlot(
  media: KioskConfigurationMedia[] | undefined,
  role: string,
): KioskImageSlot | undefined {
  const item = (media ?? []).find((row) => row?.role === role);
  if (!item || item.mediaType?.toLowerCase() === "video") return undefined;
  if (!item.mid && !item.url) return undefined;
  return {
    ...(item.mid ? { imageId: item.mid } : {}),
    ...(item.url ? { imageUrl: item.url } : {}),
  };
}

/**
 * `"optional"` → `"Optional"`, `"off"` → `"Disabled"`. Anything unrecognised is left ABSENT,
 * which `contactPolicy()` reads as `Disabled` — the safe default for a value a newer backend
 * might invent.
 */
function toContactPolicy(captureMobile: string | undefined): KioskContactPolicy | undefined {
  switch (captureMobile?.toLowerCase()) {
    case "mandatory":
      return "Mandatory";
    case "optional":
      return "Optional";
    case "off":
      return "Disabled";
    default:
      return undefined;
  }
}

/** A positive number of seconds, or nothing — so a `0` or a junk value falls back downstream. */
function positiveSeconds(value: unknown): number | undefined {
  return typeof value === "number" && value > 0 ? value : undefined;
}

export function toKioskConfig(response: KioskConfigurationResponse): KioskConfig {
  const idleTimeoutSeconds = positiveSeconds(response.screensaver?.settings?.idleTimeoutSeconds);
  const screensaverSlideSeconds = positiveSeconds(
    response.screensaver?.settings?.slideDurationSeconds,
  );
  const bannerSlideSeconds = positiveSeconds(response.banners?.settings?.slideDurationSeconds);
  const customerPhoneNumber = toContactPolicy(response.customer?.captureMobile);
  const layout = response.branding?.settings?.layout;
  const digitalBill = response.customer?.digitalBill;
  const logo = toImageSlot(response.branding?.media, "logo");
  const itemPlaceholder = toImageSlot(response.catalogue?.media, "itemPlaceholder");

  return {
    // `buildKioskTheme` falls back to the default brand colour for an empty or invalid hex.
    primaryColor: response.branding?.settings?.primaryColor ?? "",
    banners: toBanners(response.banners?.media),
    screensaver: toBanners(response.screensaver?.media),
    paymentModeIds: [],
    ...(customerPhoneNumber ? { customerPhoneNumber } : {}),
    ...(idleTimeoutSeconds ? { idleTimeoutSeconds } : {}),
    ...(screensaverSlideSeconds ? { screensaverSlideSeconds } : {}),
    ...(bannerSlideSeconds ? { bannerSlideSeconds } : {}),
    ...(layout ? { layout } : {}),
    ...(typeof digitalBill === "boolean" ? { digitalBill } : {}),
    ...(logo ? { logo } : {}),
    ...(itemPlaceholder ? { itemPlaceholder } : {}),
  };
}
